import React, { useState, useEffect, useCallback } from 'react';

import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Modal, Button } from 'react-bootstrap';

/* Load an image element from a URL */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = url;
  });

/* Produce a cropped square JPEG File from the source + crop area */
async function getCroppedFile(src, area, fileName) {
  const image = await createImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = area.width;
  canvas.height = area.height;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    area.width,
    area.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(new File([blob], fileName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.92
    );
  });
}

/*
  Square image cropper. Drag to reposition, slider to zoom. On apply it returns
  a cropped File (via onApply) so the product's images are framed correctly.
*/
function ImageCropperModal({ show, src, onApply, onHide }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [src]);

  const onCropComplete = useCallback((_, area) => {
    setAreaPixels(area);
  }, []);

  const apply = async () => {
    if (!areaPixels || !src) return;
    try {
      setWorking(true);
      const file = await getCroppedFile(
        src,
        areaPixels,
        `product-${Date.now()}.jpg`
      );
      onApply(file);
    } catch (e) {
      console.log('Crop failed:', e);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>✂️ Adjust Image</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div
          style={{
            position: 'relative',
            height: 340,
            background: '#0f172a',
            borderRadius: 12,
            overflow: 'hidden'
          }}
        >
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="d-flex align-items-center gap-3 mt-3">
          <span style={{ fontSize: '1.1rem' }}>🔍</span>
          <input
            type="range"
            className="form-range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
        <small className="text-muted">
          Drag to reposition, slide to zoom. The visible square is what gets
          saved.
        </small>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="warning" onClick={apply} disabled={working}>
          {working ? 'Applying...' : 'Apply Crop'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ImageCropperModal;
